/**
 * ScanV2Page
 *
 * Guided, auto-capture scan flow. Self-contained: handles preflight (client
 * data fetch, height/weight form, startTask), device permissions, level check,
 * pose guidance, and both photo uploads.
 *
 * Route: /scan/:profileID/:sessionID
 *
 * FSM states:
 *   preflight → sound_check → permissions → level_check → pose_guidance → uploading →
 *   (side_transition → level_check → pose_guidance → uploading) → done
 *
 * On backend rejection: retry_prompt → level_check (camera stays mounted).
 */

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { CaptureScreen } from "../design-system/shell/capture-screen";
import { Spinner } from "../design-system/primitives/spinner";
import { ActionButton } from "../design-system/concept/action-button";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { e2eForcePoseOk } from "../lib/e2eFlags";
import { createPortal } from "react-dom";
import Webcam from "react-webcam";

import { Context } from "../MyContext";
import { AuthProvider, startTask, uploadImage } from "../lib/http-service";
import { ClientDataHolder } from "../lib/dashboard/data";
import { ClientData } from "../lib/interfaces";
import { useAuth } from "../context/AuthContext";
import TiltLevel from "../components/scan/scanv2/TiltLevel";
import AutoCaptureRing from "../components/scan/scanv2/AutoCaptureRing";
import { useAutoCapture } from "../components/scan/scanv2/useAutoCapture";
import { useVoicePrompter, speak } from "../components/scan/scanv2/useVoicePrompter";
import { MediaPipe } from "../components/scan/MediaPipe";
import { PoseError } from "../components/scan/PoseErrorClassifier";
import { currentConfig } from "../lib/domainConfig";
import { readWeight, weightDoubt } from "../lib/scan/weight";
import { buildCaptureRecord, DeviceInfo, Permission, TiltSampler } from "../lib/capture/captureRecord";
import { readDeviceInfo } from "../lib/capture/deviceInfo";
// ── Types ────────────────────────────────────────────────────────────────────

type Pose = "front" | "right" | "back" | "left" | "flex";

type ScanState =
  | "preflight"
  | "sound_check"
  | "permissions"
  | "level_check"
  | "pose_guidance"
  | "uploading"
  | "retry_prompt"
  | "side_transition"
  | "done";

interface State {
  scanState: ScanState;
  pose: Pose;
  retryMessage: string | null;
  // How many times the current pose has been rejected in a row. Reset
  // whenever the pose advances (success or forced advance — see
  // MAX_POSE_REJECTIONS below).
  rejectedCount: number;
  // Set when a pose is force-advanced after hitting MAX_POSE_REJECTIONS, so
  // the side-transition voice effect can speak a short heads-up. Null for a
  // normal (UPLOAD_OK) advance.
  transitionNotice: string | null;
}

type Action =
  | { type: "PREFLIGHT_DONE" }
  | { type: "SOUND_CHECK_OK" }
  | { type: "SOUND_CHECK_SKIP" }
  | { type: "PERMISSIONS_GRANTED" }
  | { type: "LEVEL_STABLE" }
  | { type: "CAPTURE_FIRED" }
  | { type: "UPLOAD_OK" }
  | { type: "UPLOAD_REJECTED"; message: string }
  | { type: "RETRY_READY" }
  | { type: "SIDE_READY" }
  | { type: "LEVEL_LOST" };

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "PREFLIGHT_DONE":
      return {
        scanState: "sound_check",
        pose: "front",
        retryMessage: null,
        rejectedCount: 0,
        transitionNotice: null,
      };

    case "SOUND_CHECK_OK":
    case "SOUND_CHECK_SKIP":
      return { ...state, scanState: "permissions" };

    case "PERMISSIONS_GRANTED":
      return { ...state, scanState: "level_check" };

    case "LEVEL_STABLE":
      return { ...state, scanState: "pose_guidance" };

    case "CAPTURE_FIRED":
      return { ...state, scanState: "uploading" };

    case "UPLOAD_OK": {
      const idx = POSE_SEQUENCE.indexOf(state.pose);
      if (idx < POSE_SEQUENCE.length - 1) {
        return {
          ...state,
          scanState: "side_transition",
          pose: POSE_SEQUENCE[idx + 1],
          rejectedCount: 0,
          transitionNotice: null,
        };
      }
      return { ...state, scanState: "done", rejectedCount: 0, transitionNotice: null };
    }

    case "UPLOAD_REJECTED": {
      const rejectedCount = state.rejectedCount + 1;
      if (rejectedCount >= MAX_POSE_REJECTIONS) {
        // Dev-only collection pragmatics (design doc's "FE gates, BE flags"
        // rule): rembg already stored this frame server-side and flagged it
        // before validation ran, so nothing is lost by moving on. Advance
        // exactly as UPLOAD_OK would, rather than looping the user forever
        // on a pose that keeps failing — a human reviews the flagged frame
        // later instead.
        const idx = POSE_SEQUENCE.indexOf(state.pose);
        const notice = "Moving on — we'll check that one manually.";
        if (idx < POSE_SEQUENCE.length - 1) {
          return {
            ...state,
            scanState: "side_transition",
            pose: POSE_SEQUENCE[idx + 1],
            rejectedCount: 0,
            transitionNotice: notice,
          };
        }
        return { ...state, scanState: "done", rejectedCount: 0, transitionNotice: notice };
      }
      return { ...state, scanState: "retry_prompt", retryMessage: action.message, rejectedCount };
    }

    case "RETRY_READY":
      return { ...state, scanState: "level_check", retryMessage: null };

    case "SIDE_READY":
      return { ...state, scanState: "level_check" };

    case "LEVEL_LOST":
      return { ...state, scanState: "level_check" };

    default:
      return state;
  }
}

// ── Constants ────────────────────────────────────────────────────────────────

const LEVEL_STABLE_MS   = 1000; // time level must hold before entering pose_guidance
const RETRY_DISPLAY_MS  = 3000; // how long to show retry message before re-entering
const SIDE_PROMPT_MS    = 3000; // how long to show side-transition prompt
// Same-pose rejection cap. Per the flex-pose design's "FE gates, BE flags"
// rule (pose-registry.md rule 1), rembg stores + flags the frame even when
// it rejects it, so retrying forever buys nothing once it's this stubborn —
// move on and let a human review the flagged frame instead of stranding
// the session. Dev-only collection pragmatics.
const MAX_POSE_REJECTIONS = 3;
const ORIENTATION_AVG_WINDOW_MS = 1000; // trailing window for beta moving average
const WELCOME_TEXT      = "Welcome to Size-wall";
const INSTRUCTIONS_VIDEO_URL = "https://sizewallpublic.blob.core.windows.net/public/sw-instructions.mp4";

// ── View flow ───────────────────────────────────────────────────────────────
// Domain config decides whether this host captures all four views or front/right.
const FOUR_VIEW_MODE = currentConfig.use4views;
// Set to true to skip the 2-second countdown and capture immediately after
// 250 ms of good pose.
const INSTANT_CAPTURE = true;
const POSE_SEQUENCE: Pose[] = FOUR_VIEW_MODE
  ? ["front", "right", "left", "flex"]
  : ["front", "right"];

function pushMovingAverage(
  samples: Array<{ t: number; v: number }>,
  value: number,
  now: number,
  windowMs: number
): number {
  samples.push({ t: now, v: value });
  while (samples.length && now - samples[0].t > windowMs) {
    samples.shift();
  }
  let sum = 0;
  for (const s of samples) sum += s.v;
  return samples.length ? sum / samples.length : value;
}

// ── Component ────────────────────────────────────────────────────────────────

const ScanV2Page: React.FC = () => {
  const { profileID, sessionID } =
    useParams<{ profileID: string; sessionID: string }>();
  const history = useHistory();
  const location = useLocation();

  const { setOrientation } = useContext(Context);
  const { getAccessTokenSilently } = useAuth();

  // ── Auth ─────────────────────────────────────────────────────────────────

  const code = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("code") || undefined;
  }, [location.search]);

  const auth = useMemo(
    () =>
      new AuthProvider(
        code ? () => Promise.resolve(code) : getAccessTokenSilently,
        code,
        profileID,
        sessionID
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [profileID, sessionID, code]
  );

  const [client] = useState(() => new ClientDataHolder(profileID, auth));

  // ── Client / preflight state ──────────────────────────────────────────────

  const [clientData, setClientData] = useState<ClientData | null>(null);
  const [height, setHeight] = useState("");
  const [weight, setWeight] = useState("");
  // The profile's weight, offered as a hint — never as the field's value. It
  // is set when the client is created and only ever updated by a saved scan,
  // so pre-filling it recorded an old figure as the day's weight whenever the
  // field was left alone.
  const [lastWeight, setLastWeight] = useState<number | null>(null);
  // A weight that is in range but looks like a typo ("35" for "53", "65"
  // for "85") is asked about once; the answer holds for that exact text.
  const [weightQuestion, setWeightQuestion] = useState<string | null>(null);
  const [weightConfirmed, setWeightConfirmed] = useState<string | null>(null);
  const weightInputRef = useRef<HTMLInputElement>(null);
  const [preflightError, setPreflightError] = useState<string | null>(null);
  const [preflightBusy, setPreflightBusy] = useState(false);
  const [soundNoShown, setSoundNoShown] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [debugRecordingEnabled, setDebugRecordingEnabled] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get("debug") === "1";
  });
  const [debugSnapshotCount, setDebugSnapshotCount] = useState(0);

  // Why the profile did not load, if it did not. There was no catch here: a
  // refused request — a scan link whose sign-in failed — left the page on
  // "Loading profile…" for good, with nothing to tell the user or the coach
  // why. It was how a scan with live data on the demo looked, every time.
  const [profileError, setProfileError] = useState<string | null>(null);

  useEffect(() => {
    client
      .getClient()
      .then((data) => {
        setClientData(data);
        if (data.height) setHeight(data.height.toString());
        if (data.weight) setLastWeight(data.weight);
      })
      .catch((e: unknown) =>
        setProfileError(e instanceof Error ? e.message : "The profile did not load."),
      );
  }, [client]);

  // Gender comes from the fetched profile (source of truth).
  // Fall back to "male" until data arrives — only used after preflight completes.
  const gender = clientData?.gender ?? "male";

  const [state, dispatch] = useReducer(reducer, {
    scanState: "preflight",
    pose: "front",
    retryMessage: null,
    rejectedCount: 0,
    transitionNotice: null,
  });

  // Stable-level tracking — timer fires after LEVEL_STABLE_MS of continuous level
  const levelTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Webcam ref (react-webcam)
  const cameraRef = useRef<any>(null);

  // Pose OK from MediaPipe
  const [poseOK, setPoseOK] = useState(false);
  const [poseLoaded, setPoseLoaded] = useState(false);

  // Prevent MediaPipe from running when not needed
  const shouldRunRef = useRef(false);
  const poseRunId = `scanv2-${sessionID}-${state.pose}`;

  const shouldRunPose = useCallback(
    (id: string) => shouldRunRef.current && id === poseRunId,
    [poseRunId]
  );

  // Orientation from the DeviceOrientation event. beta drives the level logic.
  const orientationRef = useRef({ alpha: 0, beta: 0, gamma: 0 });
  const betaSamplesRef = useRef<Array<{ t: number; v: number }>>([]);
  // SF-84 capture metadata: sensor window, permission outcome, device and
  // attempt counters, read at the shutter and sent with the upload.
  const tiltSamplerRef = useRef(new TiltSampler(ORIENTATION_AVG_WINDOW_MS));
  const sensorPermissionRef = useRef<Permission>("unavailable");
  const deviceInfoRef = useRef<DeviceInfo | null>(null);
  const viewAttemptsRef = useRef<Record<string, number>>({});
  const sessionAttemptsRef = useRef(0);

  // Pose errors from MediaPipe — stored in a ref so voicePrompt can read latest without re-rendering
  const poseErrorsRef  = useRef<PoseError[]>([]);
  const poseMovingRef  = useRef(false);
  const poseV2HintRef  = useRef<string | null>(null);
  const latestKeypointsRef = useRef<{
    kpPx: [number, number][];
    videoWidth: number;
    videoHeight: number;
    poseOk: boolean;
  } | null>(null);
  const debugSnapshotStateRef = useRef<{
    key: string;
    count: number;
    lastAt: number;
  } | null>(null);

  // Voice
  const { prompt: voicePrompt, reset: voiceReset, currentText: voiceText } =
    useVoicePrompter();

  // Auto capture
  const handleCapture = useCallback(async () => {
    if (!cameraRef.current) {
      speak("Hold still while I take the photo");
      return false;
    }
    const video = cameraRef.current.video as HTMLVideoElement | null;
    const nativeW = video?.videoWidth;
    const nativeH = video?.videoHeight;
    const photo: string = nativeW && nativeH
      ? cameraRef.current.getScreenshot({ width: nativeW, height: nativeH })
      : cameraRef.current.getScreenshot();
    if (!photo) {
      speak("Hold still while I take the photo");
      return false;
    }
    dispatch({ type: "CAPTURE_FIRED" });
    speak("Photo taken, processing");

    // SF-84: describe how this photo was taken. Never allowed to block the upload.
    let capture: object | undefined;
    try {
      const view = state.pose;
      const attempt = (viewAttemptsRef.current[view] ?? 0) + 1;
      viewAttemptsRef.current[view] = attempt;
      sessionAttemptsRef.current += 1;
      capture = buildCaptureRecord({
        view,
        attempt,
        sessionAttempt: sessionAttemptsRef.current,
        rejectedSoFar: state.rejectedCount,
        now: new Date(),
        device: deviceInfoRef.current ?? {
          userAgent: navigator.userAgent,
          model: null,
          platform: null,
          mobile: null,
          screen: { width: window.screen.width, height: window.screen.height, dpr: window.devicePixelRatio },
        },
        video: { width: nativeW ?? null, height: nativeH ?? null },
        tilt: tiltSamplerRef.current.snapshot(Date.now(), orientationRef.current.beta, sensorPermissionRef.current),
        app: { commit: __APP_COMMIT__, buildTime: __APP_BUILD_TIME__ },
        poseOk: latestKeypointsRef.current?.poseOk ?? null,
      });
    } catch (err) {
      console.warn("capture record failed", err);
    }

    // Upload
    const ori = orientationRef.current;
    try {
      await client.addResourceToSession(
        sessionID,
        `${state.pose}_angle`,
        { beta: ori.beta, alpha: ori.alpha, gamma: ori.gamma },
        true
      );

      const response = await uploadImage(
        profileID,
        sessionID,
        state.pose,
        photo,
        gender,
        state.pose === "front",
        auth,
        capture
      );

      const body = await response.json();

      if (response.status >= 400 || body.status === "rejected") {
        const isDev =
          window.location.hostname.includes("dev") ||
          window.location.hostname.includes("localhost");
        const reason = isDev
          ? body.reason || body.error || "Upload failed"
          : "The photo could not be processed. Please try again.";
        dispatch({ type: "UPLOAD_REJECTED", message: reason });
      } else {
        speak("Photo processed");
        dispatch({ type: "UPLOAD_OK" });
      }
    } catch (err) {
      dispatch({ type: "UPLOAD_REJECTED", message: "Upload failed. Please try again." });
    }
    return true;
  }, [client, sessionID, profileID, gender, auth, state.pose, state.rejectedCount]);

  const recordDebugSnapshot = useCallback(
    async (hint: string) => {
      if (!debugRecordingEnabled) return;
      if (!cameraRef.current) return;

      const now = Date.now();
      const viewKey = `${sessionID}:${state.pose}`;
      const debugState = debugSnapshotStateRef.current;
      if (!debugState || debugState.key !== viewKey) {
        debugSnapshotStateRef.current = { key: viewKey, count: 0, lastAt: 0 };
      }

      const activeState = debugSnapshotStateRef.current;
      if (!activeState) return;
      if (activeState.count >= 10) return;
      if (activeState.lastAt && now - activeState.lastAt < 1500) return;

      activeState.count += 1;
      activeState.lastAt = now;
      setDebugSnapshotCount(activeState.count);

      const screenshot = cameraRef.current.getScreenshot();
      const payload = {
        createdAt: new Date().toISOString(),
        hint,
        pose: state.pose,
        scanState: state.scanState,
        poseOk: latestKeypointsRef.current?.poseOk ?? poseOK,
        poseErrors: poseErrorsRef.current,
        moving: poseMovingRef.current,
        orientation: orientationRef.current,
        keypoints: latestKeypointsRef.current?.kpPx ?? [],
        videoWidth: latestKeypointsRef.current?.videoWidth ?? null,
        videoHeight: latestKeypointsRef.current?.videoHeight ?? null,
      };

      try {
        await client.addResourceToSession(
          sessionID,
          `debug_back_snapshot_${now}`,
          payload,
          true
        );
        if (screenshot) {
          await client.addResourceToSession(
            sessionID,
            `debug_back_snapshot_${now}_image`,
            { dataUrl: screenshot },
            true
          );
        }
      } catch (error) {
        console.warn("Failed to save debug snapshot", error);
      }
    },
    [client, debugRecordingEnabled, poseOK, sessionID, state.pose, state.scanState]
  );

  useEffect(() => {
    if (!debugRecordingEnabled) {
      debugSnapshotStateRef.current = null;
      setDebugSnapshotCount(0);
      return;
    }

    debugSnapshotStateRef.current = {
      key: `${sessionID}:${state.pose}`,
      count: 0,
      lastAt: 0,
    };
    setDebugSnapshotCount(0);
  }, [debugRecordingEnabled, sessionID, state.pose]);

  const handleDebugSnapshot = useCallback(() => {
    void recordDebugSnapshot("manual debug snapshot");
  }, [recordDebugSnapshot]);

  // e2eForcePoseOk is false in every real browser. It lets the e2e suite drive
  // capture without a detectable body in frame; rembg still validates the
  // uploaded image, so this changes when the shutter fires, not what is
  // accepted. See src/common/e2eFlags.ts.
  const { progress: captureProgress, armed, reset: captureReset } = useAutoCapture(
    poseOK || e2eForcePoseOk(),
    handleCapture,
    state.scanState === "pose_guidance",
    INSTANT_CAPTURE
  );

  // Feed armed state to voice prompter every time something changes.
  // Suppress "stand still" in instant-capture mode — the shot fires in ~250ms anyway.
  useEffect(() => {
    voicePrompt(poseErrorsRef.current, INSTANT_CAPTURE ? false : armed, poseMovingRef.current, poseV2HintRef.current);
  }, [armed]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Permissions ─────────────────────────────────────────────────────────────

  // iOS requires DeviceMotionEvent.requestPermission() to be called from a
  // On localhost we skip motion sensor setup entirely — no sensor on laptops.
  const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";

  // iOS Safari 13+ requires DeviceOrientationEvent.requestPermission() called from
  // a user-gesture tap.  On Android/desktop the browser fires events without a prompt.
  const needsIosMotionPrompt =
    !isLocalhost &&
    typeof DeviceOrientationEvent !== "undefined" &&
    typeof (DeviceOrientationEvent as any).requestPermission === "function";

  // Keep a stable ref to the handler so we can remove the exact same function on unmount.
  const orientationHandlerRef = useRef<((e: DeviceOrientationEvent) => void) | null>(null);
  const motionHandlerRef = useRef<((e: DeviceMotionEvent) => void) | null>(null);

  const startOrientationListener = () => {
    if (orientationHandlerRef.current) return; // already registered
    // Use the raw DeviceOrientation beta angle directly. This is far more stable
    // across devices than the quaternion-derived path it replaced. We still apply
    // a trailing moving average to beta so it doesn't jitter out of range mid-scan.
    betaSamplesRef.current = [];
    const handler = (e: DeviceOrientationEvent) => {
      const alpha = e.alpha ?? 0;
      const beta  = e.beta  ?? 0;
      const gamma = e.gamma ?? 0;

      const now = Date.now();
      tiltSamplerRef.current.addOrientation(e, now);
      const avgBeta = pushMovingAverage(
        betaSamplesRef.current,
        beta,
        now,
        ORIENTATION_AVG_WINDOW_MS
      );

      orientationRef.current = { alpha, beta: avgBeta, gamma };
      setOrientation({ alpha, beta: avgBeta, gamma });
    };
    orientationHandlerRef.current = handler;
    window.addEventListener("deviceorientation", handler, true);

    // Gravity vector for the SF-84 capture record: gives pitch and roll without
    // the Euler-angle breakdown of gamma when the phone is upright.
    const motionHandler = (e: DeviceMotionEvent) => {
      tiltSamplerRef.current.addMotion(e.accelerationIncludingGravity, Date.now());
    };
    motionHandlerRef.current = motionHandler;
    window.addEventListener("devicemotion", motionHandler, true);
  };

  const grantPermissions = async () => {
    // iOS Safari: request permission from user-gesture context, then start listener.
    if (needsIosMotionPrompt) {
      try {
        const perm = await (DeviceOrientationEvent as any).requestPermission();
        sensorPermissionRef.current = perm === "granted" ? "granted" : "denied";
        if (perm !== "granted") {
          console.warn("Motion permission denied");
        }
      } catch {
        sensorPermissionRef.current = "error";
        /* proceed anyway */
      }
      // DeviceMotion (gravity) has its own permission call on iOS. One grant
      // covers both, so this normally resolves without a second prompt.
      try {
        const motionEvent = (window as any).DeviceMotionEvent;
        if (motionEvent && typeof motionEvent.requestPermission === "function") {
          await motionEvent.requestPermission();
        }
      } catch {
        /* the record falls back to orientation angles (tilt.source) */
      }
    } else {
      sensorPermissionRef.current =
        typeof DeviceOrientationEvent === "undefined" ? "unavailable" : "not-required";
    }

    startOrientationListener();
    dispatch({ type: "PERMISSIONS_GRANTED" });
  };

  // On Android/desktop there is no permission prompt — auto-proceed and start listener.
  // On localhost, also skip straight through (no sensor on laptops).
  useEffect(() => {
    if (state.scanState !== "permissions") return;
    if (!needsIosMotionPrompt) {
      grantPermissions();
    }
  }, [state.scanState]); // eslint-disable-line react-hooks/exhaustive-deps

  // On localhost, skip level_check entirely — dispatch LEVEL_STABLE immediately.
  useEffect(() => {
    if (!isLocalhost) return;
    if (state.scanState !== "level_check") return;
    dispatch({ type: "LEVEL_STABLE" });
  }, [state.scanState]); // eslint-disable-line react-hooks/exhaustive-deps

  // SF-84: phone identity, read once (UA Client Hints are async).
  useEffect(() => {
    readDeviceInfo(navigator as any, window.screen, window.devicePixelRatio)
      .then((d) => { deviceInfoRef.current = d; })
      .catch(() => { /* the record falls back to the UA alone */ });
  }, []);

  // Attempt counters are per scan session.
  useEffect(() => {
    viewAttemptsRef.current = {};
    sessionAttemptsRef.current = 0;
  }, [sessionID]);

  // Remove the deviceorientation listener only on unmount.
  useEffect(() => {
    return () => {
      if (orientationHandlerRef.current) {
        window.removeEventListener("deviceorientation", orientationHandlerRef.current, true);
        orientationHandlerRef.current = null;
      }
      if (motionHandlerRef.current) {
        window.removeEventListener("devicemotion", motionHandlerRef.current, true);
        motionHandlerRef.current = null;
      }
    };
  }, []);

  // ── Preflight submit ─────────────────────────────────────────────────────────

  /** `confirmedWeight` is the weight text the user just said yes to. */
  const handleStartScan = async (confirmedWeight?: string) => {
    if (!clientData) return;
    if (!height) {
      setPreflightError("Please enter your height before scanning.");
      return;
    }
    const weighed = readWeight(weight, currentConfig.requiresWeight);
    if (!weighed.ok) {
      setPreflightError(weighed.error);
      return;
    }
    const doubt =
      weighed.kg === undefined || weightConfirmed === weight || confirmedWeight === weight
        ? null
        : weightDoubt(weighed.kg, { heightCm: Number(height), lastKg: lastWeight });
    if (doubt) {
      setPreflightError(null);
      setWeightQuestion(doubt);
      return;
    }
    setWeightQuestion(null);
    setPreflightError(null);
    setPreflightBusy(true);
    try {
      if (levelTimerRef.current) {
        clearTimeout(levelTimerRef.current);
        levelTimerRef.current = null;
      }
      shouldRunRef.current = false;
      setPoseOK(false);
      setPoseLoaded(false);
      poseErrorsRef.current = [];
      poseMovingRef.current = false;
      poseV2HintRef.current = null;
      orientationRef.current = { alpha: 0, beta: 0, gamma: 0 };
      betaSamplesRef.current = [];
      voiceReset();   // calls speechSynthesis.cancel() inside
      captureReset();
      // Unlock audio and greet. Wrapped in setTimeout so Chrome has time to process
      // the cancel() from voiceReset before we queue the next utterance — calling
      // speak() in the same synchronous frame as cancel() silently drops the speak.
      setTimeout(() => speak(WELCOME_TEXT), 100);

      let updated = clientData;
      if (height !== clientData.height?.toString()) {
        updated = await client.updateClient({ height: parseInt(height, 10) });
        setClientData(updated);
      }
      await startTask(updated, weighed.kg, sessionID, auth);
      dispatch({ type: "PREFLIGHT_DONE" });
    } catch (err: any) {
      setPreflightError(err?.message ?? "Setup failed. Please try again.");
    } finally {
      setPreflightBusy(false);
    }
  };

  // ── Level stable callback ────────────────────────────────────────────────────

  const handleLevelStable = useCallback(
    (isLevel: boolean) => {
      if (state.scanState === "level_check") {
        if (isLevel) {
          // Start timer on first stable call; ignore if already running
          if (!levelTimerRef.current) {
            levelTimerRef.current = setTimeout(() => {
              levelTimerRef.current = null;
              dispatch({ type: "LEVEL_STABLE" });
            }, LEVEL_STABLE_MS);
          }
        } else {
          // Level lost — cancel pending timer
          if (levelTimerRef.current) {
            clearTimeout(levelTimerRef.current);
            levelTimerRef.current = null;
          }
        }
      }
      // Note: LEVEL_LOST during pose_guidance is handled by the tilt monitor
      // interval with its own debounce — not from the TiltLevel callback.
    },
    [state.scanState]
  );

  // ── MediaPipe run gating ─────────────────────────────────────────────────────

  useEffect(() => {
    shouldRunRef.current = state.scanState === "pose_guidance";
    if (state.scanState === "pose_guidance") {
      setPoseOK(false);
      captureReset();
    } else {
      // Clear stale errors/hints so they don't bleed into the next pose phase
      poseErrorsRef.current = [];
      poseMovingRef.current = false;
      poseV2HintRef.current = null;
    }
  }, [state.scanState, captureReset]);

  // ── Tilt monitor during pose_guidance ───────────────────────────────────────
  // Poll orientationRef every 300 ms; if tilt goes out of tolerance while the
  // user is in pose_guidance, send them back to level_check.
  // Skipped on localhost (no motion sensor on laptops).
  useEffect(() => {
    if (state.scanState !== "pose_guidance") return;
    if (isLocalhost) return;

    // Only interrupt for significant physical tilt — matches TiltLevel's release
    // threshold so the two are consistent. Roll is not checked (too unreliable).
    const BETA_TOL  = 12.0;
    let outCount = 0; // consecutive out-of-tolerance samples before triggering

    const id = setInterval(() => {
      const { beta } = orientationRef.current;
      const betaErr = Math.abs(beta - 90);
      const out = betaErr > BETA_TOL;
      if (out) {
        outCount++;
        if (outCount >= 8) { // ~2.4 s of continuous tilt before interrupting
          dispatch({ type: "LEVEL_LOST" });
        }
      } else {
        outCount = 0;
      }
    }, 300);

    return () => clearInterval(id);
  }, [state.scanState]);

  // ── Retry timer ──────────────────────────────────────────────────────────────

  useEffect(() => {
    if (state.scanState !== "retry_prompt") return;
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
      setTimeout(() => speak("Make sure you are well lit and not wearing baggy clothing"), 100);
    }
    const t = setTimeout(() => {
      voiceReset();
      captureReset();
      dispatch({ type: "RETRY_READY" });
    }, RETRY_DISPLAY_MS);
    return () => clearTimeout(t);
  }, [state.scanState, voiceReset, captureReset]);

  // ── Side transition timer ────────────────────────────────────────────────────

  useEffect(() => {
    if (state.scanState !== "side_transition") return;
    voiceReset();
    captureReset();
    // Speak the side-view prompt. setTimeout avoids Chrome's cancel→speak same-frame drop.
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
      // A rejection-cap advance (see MAX_POSE_REJECTIONS in the reducer)
      // sets transitionNotice; prepend that short heads-up to the normal
      // per-pose transition line, spoken through this same mechanism.
      const notice = state.transitionNotice ? `${state.transitionNotice} ` : "";
      const voiceText = `${notice}${TRANSITION_VOICE[state.pose]}`.trim();
      if (voiceText) setTimeout(() => speak(voiceText), 100);
    }
    const t = setTimeout(() => dispatch({ type: "SIDE_READY" }), SIDE_PROMPT_MS);
    return () => clearTimeout(t);
  }, [state.scanState, state.pose, state.transitionNotice, voiceReset, captureReset]);

  // ── Done → navigate to result ────────────────────────────────────────────────

  useEffect(() => {
    if (state.scanState === "done") {
      // Trigger model optimisation (same as V1)
      client.addResourceToSession(sessionID, "ready", { createdBy: "app-v2" }, true).catch(
        () => {}
      );
      // The code rides along: the result page offers "Scan again" after a
      // discard, and a client on their own phone signs in with nothing else.
      history.push(
        `/sessions/${profileID}/${sessionID}/${gender}/result${code ? `?code=${encodeURIComponent(code)}` : ""}`,
      );
    }
  }, [state.scanState, client, sessionID, profileID, gender, history, code]);

  // ── Render helpers ───────────────────────────────────────────────────────────

  const poseLabel = { front: "Front", right: "Right side", back: "Back", left: "Left side", flex: "Flex" }[state.pose];

  // back/left reuse front/right MediaPipe logic respectively — no model changes needed.
  // flex passes through untouched — MediaPipe/PoseCheckerV2 handle it natively.
  const mediaPipePose: "front" | "right" | "flex" =
    state.pose === "back" ? "front"
    : state.pose === "left" ? "right"
    : state.pose;

  // Webcam is always mirrored (selfie view). MediaPipe mapping handles back/left logic.
  const webcamMirrored = true;

  // Voice + UI text for each transition (state.pose is the NEXT pose when in side_transition).
  //
  // The flex lines say OPEN HANDS on purpose. STAR has no articulated fingers,
  // so no training sample can contain a clenched fist — prompting one puts a
  // shape in front of the camera that the training distribution cannot
  // represent. Decided 2026-09-14, see sizewall-docs/reference/pose-registry.md.
  // "fists up" is the old wording; do not restore it.
  const TRANSITION_VOICE: Record<Pose, string> = {
    front: "",
    right: "Turn to your right, arms at your side",
    back:  "Turn your back to the camera, arms angled slightly out from your body",
    left:  "Turn to your left, arms at your side",
    flex:  "Last one! Arms up like a bodybuilder, but with your hands open.",
  };
  const TRANSITION_UI: Record<Pose, { icon: string; heading: string; sub: string }> = {
    front: { icon: "", heading: "", sub: "" },
    right: { icon: "↩️", heading: "Turn to your right",              sub: "Arms at your side" },
    back:  { icon: "🔄", heading: "Turn your back to the camera",    sub: "Arms slightly out from your body" },
    left:  { icon: "↪️", heading: "Turn to your left",               sub: "Arms at your side" },
    flex:  { icon: "💪", heading: "Last one — flex!",                 sub: "Face the camera, upper arms out, hands open" },
  };

  // Camera is always mounted once past preflight/permissions to avoid
  // restart overhead; visibility controlled by CSS.
  const playTestSound = () => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    // setTimeout lets Chrome finish processing cancel() before the next speak();
    // calling speak() in the same frame as cancel() silently drops the utterance.
    setTimeout(() => speak(WELCOME_TEXT), 100);
  };

  const showCamera =
    state.scanState === "pose_guidance" ||
    state.scanState === "uploading" ||
    state.scanState === "side_transition" ||
    state.scanState === "retry_prompt" ||
    state.scanState === "level_check";

  // ── Sound check ────────────────────────────────────────────────────────────
  if (state.scanState === "sound_check") {
    return (
      <CaptureScreen>
          <div className="flex-1 bg-background flex flex-col items-center justify-center p-8 gap-6 text-center">
            <div className="text-5xl">🔊</div>
            <h1 className="text-2xl font-bold">Can you hear the sound?</h1>
            <p className="text-muted-foreground text-sm max-w-xs">
              Voice guidance will direct you through the scan. Make sure your volume is on.
            </p>

            <button
              onClick={playTestSound}
              className="px-6 py-2 rounded-full border border-primary text-primary font-medium text-base"
            >
              ▶ Play again
            </button>

            {!soundNoShown ? (
              <div className="flex flex-col gap-3 w-full max-w-xs">
                <button
                  onClick={() => dispatch({ type: "SOUND_CHECK_OK" })}
                  className="w-full py-3 rounded-full bg-primary text-primary-foreground font-semibold text-lg"
                >
                  Yes, I can hear it
                </button>
                <button
                  onClick={() => setSoundNoShown(true)}
                  className="w-full py-2 rounded-full border border-muted text-muted-foreground text-base"
                >
                  No sound
                </button>
              </div>
            ) : (
              <div className="flex flex-col gap-3 w-full max-w-xs">
                <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 text-left text-sm text-amber-800 space-y-1">
                  <p className="font-semibold">Tips to fix sound:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Turn up your device volume</li>
                    <li>Check your phone is not on silent / mute</li>
                    <li>Remove headphones if connected</li>
                  </ul>
                </div>
                <button
                  onClick={() => { setSoundNoShown(false); playTestSound(); }}
                  className="w-full py-3 rounded-full bg-primary text-primary-foreground font-semibold text-lg"
                >
                  Try again
                </button>
                <button
                  onClick={() => dispatch({ type: "SOUND_CHECK_SKIP" })}
                  className="w-full py-2 rounded-full border border-muted text-muted-foreground text-base"
                >
                  Continue without sound
                </button>
              </div>
            )}
          </div>
      </CaptureScreen>
    );
  }

  // ── Preflight (full-screen form) ──────────────────────────────────────────
  if (state.scanState === "preflight") {
    return (
      <CaptureScreen>
          <div className="flex-1 bg-background flex flex-col items-center justify-center p-6 gap-6">
            {!clientData && profileError ? (
              <div data-testid="pf-profile-error" className="flex max-w-xs flex-col items-center gap-2 text-center">
                <p className="font-semibold">This scan could not start</p>
                <p className="text-sm text-muted-foreground">{profileError}</p>
                <p className="text-sm text-muted-foreground">
                  If you opened a scan link, ask for a new one — it may have expired.
                </p>
              </div>
            ) : !clientData ? (
              <div className="flex flex-col items-center gap-3">
                <Spinner size={28} />
                <p className="text-sm text-muted-foreground">Loading profile…</p>
              </div>
            ) : (
              <>
                <h1 className="text-2xl font-bold text-center">
                  {clientData.firstName ? `Hi ${clientData.firstName}!` : "Ready to scan?"}
                </h1>
                <p className="text-muted-foreground text-center text-sm max-w-xs">
                  Confirm your measurements, then we'll guide you through the scan.
                </p>

                <div className="w-full max-w-xs space-y-4">
                  {/* Height — hidden if already known */}
                  {!clientData?.height && (
                  <div className="space-y-1">
                    <label htmlFor="pf-height" className="block text-sm font-medium">
                      Height (cm) *
                    </label>
                    <input
                      id="pf-height"
                      type="number"
                      value={height}
                      onChange={(e) => setHeight(e.target.value)}
                      placeholder="e.g. 175"
                      className="w-full border rounded-md px-3 py-2 text-center text-lg focus:outline-none focus:ring-2 focus:ring-primary bg-white text-gray-900"
                    />
                  </div>
                  )}

                  {/* Weight today. Required where body composition is the
                      point (see Config.requiresWeight); never pre-filled. */}
                  <div className="space-y-1">
                    <label htmlFor="pf-weight" className="block text-sm font-medium">
                      Weight today (kg){" "}
                      {currentConfig.requiresWeight ? (
                        "*"
                      ) : (
                        <span className="text-muted-foreground">(optional)</span>
                      )}
                    </label>
                    <input
                      id="pf-weight"
                      type="text"
                      inputMode="decimal"
                      value={weight}
                      ref={weightInputRef}
                      onChange={(e) => {
                        setWeight(e.target.value);
                        setWeightQuestion(null);
                      }}
                      placeholder={lastWeight ? `Last time ${lastWeight}` : "e.g. 70"}
                      className="w-full border rounded-md px-3 py-2 text-center text-lg focus:outline-none focus:ring-2 focus:ring-primary bg-white text-gray-900"
                    />
                    {lastWeight !== null && weight === "" && (
                      <button
                        type="button"
                        data-testid="pf-weight-same"
                        onClick={() => setWeight(String(lastWeight))}
                        className="w-full text-center text-sm font-medium text-primary hover:underline"
                      >
                        Same as last time: {lastWeight} kg
                      </button>
                    )}
                  </div>
                </div>

                {preflightError && (
                  <p className="text-destructive text-sm text-center">{preflightError}</p>
                )}

                {weightQuestion && (
                  <div
                    data-testid="pf-weight-question"
                    role="alert"
                    className="w-full max-w-xs space-y-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-center text-sm text-gray-900"
                  >
                    <p>{weightQuestion}</p>
                    <div className="flex justify-center gap-2">
                      <button
                        type="button"
                        data-testid="pf-weight-change"
                        onClick={() => {
                          setWeightQuestion(null);
                          weightInputRef.current?.focus();
                          weightInputRef.current?.select();
                        }}
                        className="rounded-full border px-4 py-1.5 font-medium"
                      >
                        Change it
                      </button>
                      <button
                        type="button"
                        data-testid="pf-weight-yes"
                        onClick={() => {
                          setWeightConfirmed(weight);
                          setWeightQuestion(null);
                          void handleStartScan(weight);
                        }}
                        className="rounded-full bg-primary px-4 py-1.5 font-medium text-primary-foreground"
                      >
                        Yes, {weight.trim()} kg
                      </button>
                    </div>
                  </div>
                )}

                <button
                  onClick={() => void handleStartScan()}
                  disabled={preflightBusy || !clientData}
                  className="w-full max-w-xs py-3 rounded-full bg-primary text-primary-foreground font-semibold text-lg disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {preflightBusy ? (
                    <>
                      <Spinner size={20} />
                      Setting up…
                    </>
                  ) : (
                    "Start Scan"
                  )}
                </button>

                <button
                  onClick={() => setShowVideoModal(true)}
                  className="w-full max-w-xs py-3 rounded-full border border-primary text-primary font-semibold text-base"
                >
                  View Instructions
                </button>

              </>
            )}
          </div>

        {/* Render modal in document.body so no parent toolbar/stacking context can cover it. */}
        {showVideoModal && typeof document !== "undefined" && createPortal(
          <div className="fixed inset-0 z-[2147483647] bg-black">
            {/* Video container fills viewport and keeps aspect ratio */}
            <div className="absolute inset-0 flex items-center justify-center bg-black">
              <video
                className="w-full h-full object-contain"
                controls
                autoPlay
                playsInline
                src={INSTRUCTIONS_VIDEO_URL}
              >
                Your browser does not support the video tag.
              </video>
            </div>

            {/* Top gradient ensures close button remains readable on bright frames */}
            <div className="absolute top-0 left-0 right-0 h-28 bg-gradient-to-b from-black/85 to-transparent pointer-events-none" />

            {/* Close button remains visible even with browser/toolbars and notches */}
            <button
              onClick={() => setShowVideoModal(false)}
              className="absolute right-3 z-[2147483647] flex items-center gap-2 px-5 py-2 rounded-full bg-black/75 text-white text-base font-semibold border border-white/30 active:bg-black/90"
              style={{ top: "calc(env(safe-area-inset-top, 0px) + 8px)" }}
              aria-label="Close instructions"
            >
              ✕ Close
            </button>
          </div>,
          document.body
        )}
      </CaptureScreen>
    );
  }

  return (
    <CaptureScreen>
        {/* flex-1, not h-full. The camera and its overlays are absolutely
            positioned, so this box is the only thing giving them a size — and
            h-full resolved against CaptureScreen, whose height comes from
            flexing inside a min-h column and is not definite. The box was
            therefore as tall as its one in-flow child: the level widget,
            about 60 % of an iPhone screen; then, once the level went green
            and the widget unmounted, nothing — 0 px, and the video with it.
            Flexing fills the screen whatever is in flow. */}
        <div className="relative w-full flex-1 min-h-0 bg-black overflow-hidden flex flex-col items-center justify-center">

          {/* ── Camera (always mounted when showCamera) ─────────────────── */}
          {showCamera && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                opacity: state.scanState === "pose_guidance" ? 1 : 0.25,
                transition: "opacity 400ms",
                pointerEvents: "none",
              }}
            >
              {currentConfig.isDev && (
                <div
                  className="absolute top-3 right-3 z-30 pointer-events-auto"
                  style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
                >
                  <button
                    type="button"
                    onClick={() => setDebugRecordingEnabled((enabled) => !enabled)}
                    className={`rounded-full px-4 py-2 text-xs font-semibold tracking-wide border shadow-lg backdrop-blur ${
                      debugRecordingEnabled
                        ? "bg-emerald-500/90 text-white border-emerald-300"
                        : "bg-black/60 text-white border-white/30"
                    }`}
                    aria-pressed={debugRecordingEnabled}
                    aria-label={debugRecordingEnabled ? "Disable debug snapshots" : "Enable debug snapshots"}
                  >
                    Debug {debugRecordingEnabled ? `ON ${debugSnapshotCount}/10` : "OFF"}
                  </button>
                </div>
              )}

              <Webcam
                ref={cameraRef}
                audio={false}
                screenshotFormat="image/jpeg"
                videoConstraints={{ facingMode: "user", width: { ideal: 1920 }, height: { ideal: 1080 } }}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                mirrored={webcamMirrored}
              />
              {/* MediaPipe skeleton overlay — also draws silhouette guidance during pose_guidance */}
              <MediaPipe
                id={poseRunId}
                pose={mediaPipePose}
                mirrored={webcamMirrored}
                flipped={state.pose === "left"}
                back={state.pose === "back"}
                showGuidance={state.scanState === "pose_guidance"}
                setOK={(ok) => {
                  setPoseOK(ok);
                  voicePrompt(
                    poseErrorsRef.current,
                    armed,
                    poseMovingRef.current,
                    poseV2HintRef.current
                  );
                }}
                setErrors={(errors) => {
                  poseErrorsRef.current = isLocalhost
                    ? errors.filter((e) => e.id !== "wrong_orientation")
                    : errors;
                }}
                setMoving={(moving) => {
                  poseMovingRef.current = moving;
                }}
                setV2Hint={(hint) => {
                  poseV2HintRef.current = hint;
                  if (
                    hint === "Turn your back to the camera" ||
                    hint === "Step back from the camera"
                  ) {
                    void recordDebugSnapshot(hint);
                  }
                }}
                setLoaded={setPoseLoaded}
                shouldRunPose={shouldRunPose}
                onKeypoints={(kpPx, videoWidth, videoHeight, poseOk) => {
                  latestKeypointsRef.current = { kpPx, videoWidth, videoHeight, poseOk };
                }}
              />

              {/* Auto-capture ring */}
              <AutoCaptureRing progress={captureProgress} />
            </div>
          )}

          {/* ── PERMISSIONS ─────────────────────────────────────────────── */}
          {state.scanState === "permissions" && (
            <div className="flex flex-col items-center gap-6 text-white z-10 px-8 text-center">
              {needsIosMotionPrompt ? (
                <>
                  <p className="text-lg font-semibold">Sensor Access Needed</p>
                  <p className="text-sm text-white/70">
                    For the best results, Sizewall uses your phone's gyroscope
                    to keep the camera level. Please tap the button below and
                    then tap <strong>Allow</strong>.
                  </p>
                  <ActionButton variant="primary" className="w-full py-3 text-sm" onClick={grantPermissions}>
                    Allow Motion Access
                  </ActionButton>
                </>
              ) : (
                <>
                  <Spinner size={28} />
                  <p className="text-base">Starting camera…</p>
                </>
              )}
            </div>
          )}

          {/* ── LEVEL CHECK ─────────────────────────────────────────────── */}
          {state.scanState === "level_check" && (
            <div className="flex flex-col items-center justify-center gap-8 px-8 z-10">
              <p className="text-white/60 text-sm tracking-widest uppercase">
                {poseLabel} view
              </p>
              <TiltLevel onStable={handleLevelStable} />
            </div>
          )}

          {/* ── POSE GUIDANCE ────────────────────────────────────────────── */}
          {state.scanState === "pose_guidance" && (
            <div
              className="absolute bottom-0 left-0 right-0 z-20 flex flex-col items-center pb-10 gap-2"
              style={{ pointerEvents: "none" }}
            >
              {/* Voice / instruction caption */}
              {voiceText && (
                <div
                  className="px-5 py-2 rounded-full text-white text-lg font-semibold text-center"
                  style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(6px)" }}
                >
                  {voiceText}
                </div>
              )}
              {!poseLoaded && (
                <div className="flex items-center gap-2 text-white/50 text-sm">
                  <Spinner size={18} />
                  Loading pose detector…
                </div>
              )}
            </div>
          )}

          {/* ── UPLOADING ────────────────────────────────────────────────── */}
          {state.scanState === "uploading" && (
            <div className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-4 bg-black/70">
              <Spinner size={48} onDark />
              <p className="text-white text-lg font-medium">Processing…</p>
            </div>
          )}

          {/* ── RETRY PROMPT ─────────────────────────────────────────────── */}
          {state.scanState === "retry_prompt" && (
            <div className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-4 px-8 bg-black/80">
              <p className="text-orange-400 text-xl font-semibold text-center">
                Let's try that again
              </p>
              {state.retryMessage && (
                <p className="text-white/70 text-sm text-center">{state.retryMessage}</p>
              )}
            </div>
          )}

          {/* ── SIDE TRANSITION ──────────────────────────────────────────── */}
          {state.scanState === "side_transition" && (() => {
            const t = TRANSITION_UI[state.pose];
            return (
              <div className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-6 bg-black/80">
                <div className="text-6xl">{t.icon}</div>
                <p className="text-white text-2xl font-bold text-center px-8">{t.heading}</p>
                <p className="text-white/70 text-lg text-center px-8">{t.sub}</p>
              </div>
            );
          })()}

          {/* ── Pose badge (top-left) ─────────────────────────────────────── */}
          {(state.scanState === "pose_guidance" || state.scanState === "level_check") && (
            <div
              className="absolute top-4 left-4 z-20 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-widest text-white"
              style={{ background: "rgba(0,0,0,0.5)" }}
            >
              {poseLabel} view
            </div>
          )}
        </div>
    </CaptureScreen>
  );
};

export default ScanV2Page;
